import { format, addYears } from 'date-fns';
import { getQuoteGroup, generateQuoteObject, generateProduct } from '../services/quotes';
import { getBrokerDetails } from '../services/broker';
import { getProducts, getProductsWithEntityCode } from '../services/getQuoteProducts';
import jwtInterceptor from './jtwInterceptor';
import { StatusNumber } from './statusNumber';
import { difference } from './constants';
import { RecordState } from './recordState';
import { Regions, regions } from './regions';
import { endpoints } from './endpoints';
import {
	QuoteGroupResponse,
	Quote,
	QuoteRatingRegion,
	QuoteCreateRequest,
	QuoteGroupCreateRequest,
	QuoteProduct,
	QuotePackage,
	Package,
	Product,
	DecisionTree,
} from '../shared/types/quotes';
import {
	mapQuoteRatingRegionsToRows,
	RegionData,
	regionDataRowsToQuoteRatingRegions,
} from '../shared/types/regionData';

/**
 * Function to update a quote's client information and related quotes with new values.
 * @param {QuoteGroupResponse} quoteGroup - The quote to be updated.
 * @param {any} quoteClient - Updated client information for the quote.
 * @param {any} values - Additional Formik values and rating information for the quote.
 * @returns {Promise} A Promise that resolves with the updated quote data if successful, or an error if the update fails.
 */
export const updateQuoteClientAndRatings = (
	quoteGroup: QuoteGroupResponse,
	quoteClient: any,
	values: any,
	ratingRegionsRows?: RegionData[]
): QuoteGroupResponse => {
	let newQuotes = quoteGroup.quotes;
	let newQuoteClient =
		quoteClient != null &&
		(quoteClient.businessNumber !== quoteGroup.quoteClient.businessNumber ||
			quoteClient.acnBusinessNumber !== quoteGroup.quoteClient.acnBusinessNumber)
			? { ...quoteClient }
			: { ...quoteGroup.quoteClient };
	// if (
	// 	quoteClient != null &&
	// 	(quoteClient.businessNumber !== quoteGroup.quoteClient.businessNumber ||
	// 		quoteClient.acnBusinessNumber !== quoteGroup.quoteClient.acnBusinessNumber)
	// ) {
	// 	// Update the quoteGroup's quoteClient if a new quoteClient is provided and the businessNumber does not match
	// 	quoteGroup.quoteClient = quoteClient;
	// }

	// if (values) {
	// 	if (
	// 		(values?.streetName ?? '').length > 0 &&
	// 		values?.streetName != quoteGroup.quoteClient.address1
	// 	) {
	// 		quoteGroup.quoteClient.address1 = values?.streetName;
	// 	}
	// 	if ((values?.city ?? '').length > 0 && values?.city != quoteGroup.quoteClient.address2) {
	// 		quoteGroup.quoteClient.address2 = values?.city;
	// 	}
	// 	if (values?.region && values?.region != quoteGroup.quoteClient.stateCode) {
	// 		quoteGroup.quoteClient.stateCode = values?.region;
	// 	}
	// 	if (
	// 		(values?.postcode ?? '').length > 0 &&
	// 		values?.postcode != quoteGroup.quoteClient.postcode
	// 	) {
	// 		quoteGroup.quoteClient.postcode = values?.postcode;
	// 	}
	// }

	const quoteRatingRegionRows = mapQuoteRatingRegionsToRows(
		quoteGroup.quotes[0].quoteRatingRegions
	);
	const ratingRegions = ratingRegionsRows ?? quoteRatingRegionRows;
	const quoteRatingRegions = regionDataRowsToQuoteRatingRegions(ratingRegions).filter(
		(reg) => reg.staffNumbers > 0 || reg.currentYearAmount > 0 || reg.lastYearAmount > 0
	);

	const updatedQuotes = newQuotes.map((q) => ({
		...q,
		quoteRatingRegions: quoteRatingRegions,
		totalTurnoverAmountCurrentYear: undefined,
		totalTurnoverAmountLastYear: undefined,
		...((!q.policyPeriodStartDate || !q.policyPeriodEndDate) && {
			policyPeriodStartDate: values.startDate,
			policyPeriodEndDate: values.endDate,
		}),
	}));

	const newQuoteGroup = {
		...quoteGroup,
		quoteClient: {
			...newQuoteClient,
			...((values?.streetName ?? '').length > 0 &&
				values?.streetName !== newQuoteClient.address1 && {
					address1: values.streetName,
				}),
			...((values?.city ?? '').length > 0 &&
				values?.city !== newQuoteClient.address2 && { address2: values.city }),
			...(values?.region &&
				values?.region !== newQuoteClient.stateCode && {
					stateCode: values.region,
				}),
			...((values?.postcode ?? '').length > 0 &&
				values?.postcode !== newQuoteClient.postcode && { postcode: values.postcode }),
		},
		quotes: updatedQuotes,
	};
	return {
		...newQuoteGroup,
		// ...quoteGroup,
		// quotes: updatedQuotes,
		// quoteClient:
		// 	quoteClient.businessNumber !== quoteGroup.quoteClient.businessNumber ||
		// 	quoteClient.acnBusinessNumber !== quoteGroup.quoteClient.acnBusinessNumber
		// 		? quoteClient
		// 		: quoteGroup.quoteClient, // Update 'quoteClient' if the business number is different
	};
};

/**
 * Function to update a quote's client information.
 * @param {any} quoteGroup - The quote to be updated.
 * @param {any} quoteClient - Updated client information for the quote.
 * @returns {Promise} A Promise that resolves with the updated quote data if successful, or an error if the update fails.
 */
export const updateQuoteClient = (
	quoteGroup: QuoteGroupResponse,
	quoteClient: any
): QuoteGroupResponse => {
	// console.log('Update quote client quotes', quoteGroup.quotes);
	return {
		...quoteGroup,
		quoteClient,
	};
};

/**
 * Function to update a quote with provided products, packages, and values.
 * @param {QuoteGroupResponse} quoteGroup - The ID of the quote to update.
 * @param {Product[]} products - List of products to be included in the updated quote.
 * @param {Package[]} packages - List of packages to be included in the updated quote.
 * @param {any} values - Additional values and information for the updated quote.
 * @param {any} quoteClient - Optional updated client information for the quote.
 * @returns {Promise} A Promise that resolves with the updated quote data if successful.
 */
export const updateQuote = async (
	quoteGroup: QuoteGroupResponse,
	products: Product[],
	packages: Package[],
	values?: any,
	quoteClient?: any,
	ratingRegionsRows?: RegionData[]
): Promise<QuoteGroupResponse | QuoteGroupCreateRequest> => {
	const brokerId = localStorage.getItem('brokerId');

	const client =
		quoteClient != null &&
		(quoteClient.businessNumber !== quoteGroup.quoteClient.businessNumber ||
			quoteClient.acnBusinessNumber !== quoteGroup.quoteClient.acnBusinessNumber)
			? quoteClient
			: quoteGroup.quoteClient;

	const updatedQuoteGroup = {
		...quoteGroup,
		quoteClient: {
			...client,
			...(values && {
				...((values?.streetName ?? '').length > 0 &&
					values?.streetName != client.address1 && { address1: values.streetName }),
				...((values?.city ?? '').length > 0 &&
					values?.city != client.address2 && { address2: values.city }),
				...((values?.postcode ?? '').length > 0 &&
					values?.postcode != client.postcode && { postcode: values.postcode }),
				...(values?.region &&
					values?.region != client.stateCode && { stateCode: values?.region }),
			}),
		},
		quotes: quoteGroup.quotes.map((quote) => ({
			...quote,
			quoteProducts: quote.quoteProducts.map((product) => ({ ...product })),
			quotePackages: quote.quotePackages.map((quotePackage) => ({
				...quotePackage,
				quoteProducts: quotePackage.quoteProducts.map((product) => ({ ...product })),
			})),
		})),
	};

	// console.log("Regions[newRegion]", Regions[newRegion], "Region in obj", existingQuoteClient.stateCode, "values?.region", values?.region);
	// if (values) {
	// 	if (
	// 		(values?.streetName ?? "").length > 0 &&
	// 		values?.streetName != quoteGroup.quoteClient.address1
	// 	) {
	// 		updatedQuoteGroup.quoteClient.address1 = values?.streetName;
	// 	}
	// 	if ((values?.city ?? "").length > 0 && values?.city != quoteGroup.quoteClient.address2) {
	// 		updatedQuoteGroup.quoteClient.address2 = values?.city;
	// 	}
	// 	if (newRegion && newRegion != quoteGroup.quoteClient.stateCode) {
	// 		updatedQuoteGroup.quoteClient.stateCode = newRegion;
	// 	}
	// 	if (
	// 		(values?.postcode ?? "").length > 0 &&
	// 		values?.postcode != quoteGroup.quoteClient.postcode
	// 	) {
	// 		updatedQuoteGroup.quoteClient.postcode = values?.postcode;
	// 	}
	// 	console.log("Updated client", quoteGroup.quoteClient);
	// }

	const quoteRatingRegionRows = mapQuoteRatingRegionsToRows(
		quoteGroup?.quotes?.[0]?.quoteRatingRegions
	);
	const ratingRegions = ratingRegionsRows ?? quoteRatingRegionRows;
	const quoteRatingRegions = regionDataRowsToQuoteRatingRegions(ratingRegions).filter(
		(reg) => reg.staffNumbers > 0 || reg.currentYearAmount > 0 || reg.lastYearAmount > 0
	);

	if (quoteGroup.quoteBroker == null || Object.keys(quoteGroup.quoteBroker).length === 0) {
		// If the quoteBroker is not defined or empty, retrieve broker details and set them in the quoteGroup.
		const brokerData = await getBrokerDetails(brokerId);
		updatedQuoteGroup.quoteBroker = {
			id: brokerData.id,
			brokerFirm: null,
			email: brokerData.email,
			firstName: brokerData.firstName,
			lastName: brokerData.lastName,
		};
	}

	// The RecordState property can have only integer values listed below:
	// 1 - New
	// 2 - Deleted
	// 3 - Unchanged
	// 4 - Edited

	let newQuotes: (Quote | QuoteCreateRequest)[] = [];
	// let newQuotes: Quote[] = [];

	let existingProductIds = quoteGroup.quotes.flatMap((quote) =>
		quote.quoteProducts.map((p) => p?.id)
	);
	let existingPackageIds = quoteGroup.quotes.flatMap((quote) =>
		quote.quotePackages.map((p) => p?.id)
	);

	let productIds = products.map((p: any) => p.id);
	let packageIds = packages.map((p: any) => p.id);

	let productsToRemain = existingProductIds.filter((pId) => productIds.includes(pId));
	let productsToRemove = existingProductIds.filter((pId) => !productIds.includes(pId));

	let packagesToRemain = existingPackageIds.filter((pId) => packageIds.includes(pId));
	let packagesToRemove = existingPackageIds.filter((pId) => !packageIds.includes(pId));

	updatedQuoteGroup.quotes.forEach((quote) => {
		if (
			quote.status?.statusNumber === StatusNumber.QuoteInProgress ||
			quote.status?.statusNumber === StatusNumber.ReferralApprovedModifiable
		) {
			// Check if the quote is in progress and can be updated.
			let keepPackage = quote.quotePackages.some((p) => packagesToRemain.includes(p.id));
			// If the quote includes products or packages that need to be kept or updated:
			if (quote.quoteProducts.some((p) => productsToRemain.includes(p.id)) || keepPackage) {
				if (keepPackage) {
					// If keeping a package, create a new quote based on the package.
					let pack = packages.find((p) => p.id == quote.quotePackages[0].id);

					// let newQuote = generateQuoteObject(pack, values ?? {}, ratingRegions);
					const quoteRatingRegions = regionDataRowsToQuoteRatingRegions(
						ratingRegions
					).filter(
						(reg) =>
							reg.staffNumbers > 0 ||
							reg.currentYearAmount > 0 ||
							reg.lastYearAmount > 0
					);

					const existingQuote = {
						...quote,
						quoteProducts: quote.quoteProducts.map((p) => ({
							...p,
						})),
						quotePackages: quote.quotePackages.map((p) => ({
							...p,
							quoteProducts: pack?.products
								? p.quoteProducts.filter((prod) =>
										pack?.products.map((pp) => pp.id).includes(prod.id)
								  )
								: p.quoteProducts,
						})),
						quoteRatingRegions,
					};
					// // If there are quote products in the new package, update their decision tree if needed.
					// if (newQuote.quoteProducts.length > 0) {
					//     let product = quote.quoteProducts.find(
					//         (p: any) => p.id == newQuote.quoteProducts[0].id
					//     );
					//     if (product != null) {
					//         newQuote.quoteProducts[0].quoteProductDecisionTree =
					//             product.quoteProductDecisionTree;
					//     }
					// }
					// If there are quote packages within the new package, update their decision trees.
					// if (newQuote.quotePackages.length > 0) {
					//     let pack = quote.quotePackages.find(
					//         (p) => p.id == newQuote.quotePackages[0].id
					//     );
					//     if (pack != null) {
					//         newQuote.quotePackages[0].quoteProducts.forEach((product, i) => {
					//             let prod = pack?.quoteProducts.find((p) => p.id == product.id);
					//             if (prod != null) {
					//                 newQuote.quotePackages[0].quoteProducts[
					//                     i
					//                 ].quoteProductDecisionTree = prod.quoteProductDecisionTree;
					//             }
					//         });
					//     }
					// }

					// Push the new quote with changes to the list of quotes.
					newQuotes.push({
						...existingQuote,
						// ...quote,
						// quoteProducts: newQuote.quoteProducts,
						// quotePackages: newQuote.quotePackages,
						RecordState: RecordState.Edited, // RecordState 4 indicates an edited quote.
					});
				} else {
					newQuotes.push({
						...quote,
						RecordState: RecordState.Edited, // RecordState 4 indicates an edited quote.
					});
				}
			}
			if (
				quote.quoteProducts.some((p) => productsToRemove.includes(p.id)) ||
				quote.quotePackages.some((p) => packagesToRemove.includes(p.id))
			) {
				// If products or packages need to be removed, mark the quote as deleted (RecordState 2).
				newQuotes.push({
					...quote,
					RecordState: RecordState.Deleted, // RecordState 2 indicates a deleted quote.
				});
			}
		} else {
			// For quotes that are not in progress, keep them unchanged.
			newQuotes.push({ ...quote });
		}
	});

	let productsToAdd = difference(productIds, existingProductIds);
	products
		.filter((product) => productsToAdd.includes(product.id))
		.forEach((product) => {
			// For each product to be added, generate a new quote object and add it to the newQuotes array.
			newQuotes.push(generateQuoteObject(product, values ?? {}, ratingRegions));
		});

	let packagesToAdd = difference(packageIds, existingPackageIds);
	packages
		.filter((pack) => packagesToAdd.includes(pack.id))
		.forEach((pack) => {
			// For each package to be added, generate a new quote object and add it to the newQuotes array.
			newQuotes.push(generateQuoteObject(pack, values ?? {}, ratingRegions));
		});

	let datedQuotes = newQuotes.filter((q) => q.policyPeriodStartDate && q.policyPeriodEndDate);
	let startDate =
		datedQuotes.length > 0
			? datedQuotes[0].policyPeriodStartDate
			: format(Date.now(), "yyyy-MM-dd'T00:00:00'");
	let endDate =
		datedQuotes.length > 0
			? datedQuotes[0].policyPeriodEndDate
			: format(addYears(Date.now(), 1), "yyyy-MM-dd'T00:00:00'");

	newQuotes.forEach((q) => {
		q.quoteRatingRegions = quoteRatingRegions;
		q.totalTurnoverAmountCurrentYear = null;
		q.totalTurnoverAmountLastYear = null;
		if (!q.policyPeriodStartDate || !q.policyPeriodEndDate) {
			q.policyPeriodStartDate = startDate;
			q.policyPeriodEndDate = endDate;
		}
	});
	return {
		...updatedQuoteGroup,
		quotes: newQuotes,
	};
};

/**
 * Update a specific item (product or package) in a quote and send a POST request to update the quote.
 * @param {QuoteGroupResponse} quoteGroup - The ID of the quote to be updated.
 * @param {any} product - The product or package to update in the quote.
 * @returns {Promise} A Promise that resolves with the updated quote data if successful.
 */
export const updateQuoteItem = (
	quoteGroup: QuoteGroupResponse,
	product: QuotePackage | Package | Product | QuoteProduct
): QuoteGroupResponse => {
	// TODO: find out why product is not being updated
	console.log('Here is the product we are updating', product);
	let quoteToUpdate: Quote | undefined;
	if ('quoteProducts' in product || 'products' in product) {
		// If it's a package, find the corresponding quote
		quoteToUpdate = quoteGroup.quotes.find((q) =>
			q.quotePackages.map((p) => p.id).includes(product.id)
		);

		if (quoteToUpdate) {
			quoteToUpdate = {
				...quoteToUpdate,
				RecordState: RecordState.Edited, // Set the RecordState to "Edited"
				quotePackages: [
					...quoteToUpdate.quotePackages.filter((p) => p.id != product.id),
					product as QuotePackage, // Add the updated package
				],
			};
		}
	} else {
		// If it's a product, find the corresponding quote
		quoteToUpdate = quoteGroup.quotes.find(
			(q) =>
				q.quoteProducts.map((p) => p.id).includes(product.id) ||
				q.quotePackages.find((pack) =>
					pack.quoteProducts.map((p) => p.id).includes(product.id)
				)
		);
		if (quoteToUpdate) {
			const productIndex = quoteToUpdate.quoteProducts.findIndex((p) => p.id === product.id);
			
			let newPackage = quoteToUpdate.quotePackages.find((p) =>
				p.quoteProducts.map((p) => p.id).includes(product.id)
			);
			if (productIndex < 0 && newPackage) {
				newPackage = {
					...newPackage,
					quoteProducts: [
						...newPackage.quoteProducts.filter(
							(p) => p.id !== product.id
						),
						product as QuoteProduct,
					],
				};
			}

			quoteToUpdate = {
				...quoteToUpdate,
				RecordState: RecordState.Edited, // Set the RecordState to "Edited"
				...(productIndex >= 0 && {
					quoteProducts: [
						...quoteToUpdate.quoteProducts.filter((p) => p.id !== product.id),
						product as QuoteProduct, // Add the updated product
					],
				}),
				...(productIndex < 0 && newPackage && {
						quotePackages: [
							...quoteToUpdate.quotePackages.filter((p) => p.id !== newPackage?.id),
							newPackage, // Add the updated product
						],
					}),
			};
		}
	}
	// Create an array of new quotes by replacing the old quote with the updated quote
	let newQuotes = [
		...quoteGroup.quotes.filter((q) => q.id != quoteToUpdate?.id),
		...(quoteToUpdate
			? [
					{
						...quoteToUpdate,
						RecordState: RecordState.Edited,
					},
			  ]
			: []),
	];
	return { ...quoteGroup, quotes: newQuotes };
};

/**
 * Function to update the brokerage percentage of a quote.
 * @param {any} quoteId - The ID of the quote to be updated.
 * @param {number} brokeragePercentage - The new brokerage percentage to set for the quote.
 * @param {AbortController["signal"]} signal - (Optional) The AbortController's signal for aborting the request.
 * @returns {Promise} A Promise that resolves with the updated quote data if successful.
 */
export const updateQuoteBrokeragePercentage = (
	quoteGroup: QuoteGroupResponse,
	brokeragePercentage: number
): QuoteGroupResponse => {
	const newQuotes = quoteGroup.quotes.map((q) => {
		return {
			...q,
			brokeragePercentage: brokeragePercentage,
			RecordState: RecordState.Edited,
		};
	});
	const canUpdate = quoteGroup.quotes.some((q) => q.brokeragePercentage != brokeragePercentage);
	return { ...quoteGroup, ...(canUpdate && { quotes: newQuotes }) };
};

/**
 * Function to update the regions of a quote.
 * @param {QuoteGroupResponse} quoteGroup - The ID of the quote to be updated.
 * @param {QuoteRatingRegion[]} regions - The new list of regions to set for the quote.
 * @returns {Promise} A Promise that resolves with the updated quote data if successful.
 */
export const updateQuoteRegions = (
	quoteGroup: QuoteGroupResponse,
	regions: QuoteRatingRegion[]
): QuoteGroupResponse | QuoteGroupCreateRequest => {
	// Create a new array of quotes with updated quoteRatingRegions and RecordState set to "Edited" (4)
	const newQuotes = quoteGroup.quotes.map((q) => {
		return {
			...q,
			totalTurnoverAmountCurrentYear: null,
			totalTurnoverAmountLastYear: null,
			totalStaffNumbers: null,
			quoteRatingRegions: regions,
			RecordState: RecordState.Edited,
		};
	});
	return { ...quoteGroup, quotes: newQuotes };
};

/**
 * Updates policy period dates for quotes in a quote group.
 *
 * @param {QuoteGroupResponse} quoteGroup - The ID of the quote group to update.
 * @param {Date | null} startDate - The new start date for the policy period (optional).
 * @param {Date | null} endDate - The new end date for the policy period (optional).
 * @returns {Promise<any>} - A Promise that resolves with the updated quote data or an error.
 */
export const updateQuotePolicyDates = (
	quoteGroup: QuoteGroupResponse,
	startDate?: Date | null,
	endDate?: Date | null
): QuoteGroupResponse | QuoteGroupCreateRequest => {
	// Format the start and end dates if provided
	let formattedStartDate: string | null = null;
	if (startDate != null) {
		formattedStartDate = format(startDate, "yyyy-MM-dd'T00:00:00'");
	}
	let formattedEndDate: string | null = null;
	if (endDate != null) {
		formattedEndDate = format(endDate, "yyyy-MM-dd'T00:00:00'");
	}
	// Create new quotes with updated policy period dates
	let newQuotes = quoteGroup.quotes.map((q) => {
		return {
			...q,
			policyPeriodStartDate: formattedStartDate ?? q.policyPeriodStartDate,
			policyPeriodEndDate: formattedEndDate ?? q.policyPeriodEndDate,
			RecordState: RecordState.Edited,
		};
	});

	return { ...quoteGroup, quotes: newQuotes };
};

/**
 * Update quote answers based on the current decision tree tab.
 *
 * @param {QuoteGroupResponse} quoteGroup - The ID of the quote group to update.
 * @param {any} currentTab - The current decision tree tab or section to update.
 * @returns {Promise<any>} - A Promise that resolves with the updated quote data or an error.
 */
export const updateQuoteAnswerHandler = (
	quoteGroup: QuoteGroupResponse,
	currentTab: DecisionTree
): QuoteGroupResponse => {
	// console.log('CURRENT TAB FOR UPDATE', currentTab);
	const updatedQuoteGroup = {
		...quoteGroup,
		quotes: quoteGroup.quotes.slice().map((quote) => ({
			...quote,
			quoteProducts: quote.quoteProducts.map((product) => ({ ...product })),
			quotePackages: quote.quotePackages.map((quotePackage) => ({
				...quotePackage,
				quoteProducts: quotePackage.quoteProducts.map((product) => ({ ...product })),
			})),
		})),
	};

	if (currentTab.productId != null) {
		// console.log('Product ID', currentTab.productId);
		for (let i = 0; i < updatedQuoteGroup.quotes.length; i++) {
			// Update quote products and packages decision trees if it matches currentTab
			updatedQuoteGroup.quotes[i].quoteProducts = quoteGroup.quotes[i]?.quoteProducts?.map(
				(t) =>
					t.id == currentTab?.productId
						? { ...t, quoteProductDecisionTree: currentTab }
						: t
			);
			updatedQuoteGroup.quotes[i].quotePackages = quoteGroup.quotes[i].quotePackages.map(
				(t) => ({
					...t,
					quoteProducts: t.quoteProducts.map((p) =>
						p.id == currentTab?.productId
							? { ...p, quoteProductDecisionTree: currentTab }
							: p
					),
				})
			);
		}
	} else {
		if (currentTab.name.toUpperCase().includes('GENERAL BUSINESS QUESTIONS')) {
			// Update general business questions section
			updatedQuoteGroup.decisionTreeGeneralQuestions = currentTab;
		}
	}

	return updatedQuoteGroup;
};

/**
 * Update the decision tree for a quote based on the provided decisionTree data.
 *
 * @param {QuoteGroupResponse} quoteGroup - The ID of the quote group to update.
 * @param {any} decisionTree - The decision tree data to update the quote with.
 * @returns {Promise<any>} - A Promise that resolves with the updated quote data or an error.
 */
export const updateQuoteDecisionTree = (quoteGroup: any, decisionTree: any): QuoteGroupResponse => {
	const updatedQuoteGroup = {
		...quoteGroup,
		quotes: quoteGroup.quotes.slice().map((quote: Quote) => ({
			...quote,
			quoteProducts: quote.quoteProducts.map((product: QuoteProduct) => ({ ...product })),
			quotePackages: quote.quotePackages.map((quotePackage: QuotePackage) => ({
				...quotePackage,
				quoteProducts: quotePackage.quoteProducts.map((product: QuoteProduct) => ({
					...product,
				})),
			})),
		})),
	};

	for (let i = 0; i < decisionTree.length; i++) {
		const currentTab =
			'decisionTreeQuestions' in decisionTree[i]
				? decisionTree[i]
				: decisionTree[i]?.quoteProductDecisionTree;
		if (currentTab.productId != null) {
			// Update quote products and packages decision tree for a specific product
			for (let i = 0; i < updatedQuoteGroup.quotes.length; i++) {
				updatedQuoteGroup.quotes[i].quoteProducts = quoteGroup.quotes[
					i
				]?.quoteProducts?.map((t: QuoteProduct) =>
					t.id == currentTab?.productId
						? { ...t, quoteProductDecisionTree: currentTab }
						: t
				);
				updatedQuoteGroup.quotes[i].quotePackages = quoteGroup.quotes[i].quotePackages.map(
					(t: QuotePackage) => ({
						...t,
						quoteProducts: t.quoteProducts.map((p: QuoteProduct) =>
							p.id == currentTab?.productId
								? { ...p, quoteProductDecisionTree: currentTab }
								: p
						),
					})
				);
			}
		} else {
			if (currentTab.name.toUpperCase().includes('GENERAL BUSINESS QUESTIONS')) {
				// Update the general business questions section
				updatedQuoteGroup.decisionTreeGeneralQuestions = currentTab;
			}
		}
	}
	return updatedQuoteGroup;
};
